import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Formik } from "formik";
import * as Yup from "yup";
import { Form as FormControl, ThemeProvider } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import i18nInstance from "i18n";
import BootstrapForm from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";

import { theme } from "configs/theme";
import Button from "react-bootstrap/Button";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

import Timer from "components/Timer/Timer";
import { useVerifyOtpMutation } from "features/api/auth/authApiSlice";
import { setCredentials } from "features/api/auth/authSlice";
import jwtDecode from "jwt-decode";

import { useReports } from "hooks/useReports";
import { getInitialRole } from "utils/userDataUtiles";
import { UserRole } from "components/Reports/reportAssets/assets";
import { OtpConfirmationProps, OtpConfirmationValues } from "../types/types";

const ResendLink = () => {
  // return <Button variant="link">לא קיבלתי קוד</Button>;
  return <span className="link-text" />;
};

const validationSchema = Yup.object({
  otp: Yup.string()
    .min(6, i18nInstance.t("מינימום 6 תווים"))
    .max(6, i18nInstance.t("מקסימום 6 תווים"))
    .required(i18nInstance.t("חובה להזין את הקוד שקיבלת בטלפון")),
});

type jwtDecodedValue = {
  "http://schemas.microsoft.com/ws/2008/06/identity/claims/role":
    | string[]
    | string;
  "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name": string;
  exp: number;
  Id: string;
};

const OtpConfirmation: React.FC<OtpConfirmationProps> = ({
  phoneVerificationResponse,
  setView,
}) => {
  const initialValues: OtpConfirmationValues = { otp: "" };
  const [disabled, setDisabled] = useState<boolean>(false);
  const timerEnd = Date.now() + 10 * 60 * 1000;
  const navigate = useNavigate();
  const [verifyOtp] = useVerifyOtpMutation();
  const dispatch = useDispatch();
  const { setselectedRole } = useReports();
  const { i18n, t } = useTranslation();

  const handleVerifyOtp = async (
    values: OtpConfirmationValues,
    {
      setSubmitting,
      setFieldError,
    }: {
      setSubmitting: (boolean) => void;
      setFieldError: (key: string, value: unknown) => void;
    }
  ) => {
    setSubmitting(true);

    verifyOtp({
      Email: phoneVerificationResponse.user.email,
      otp: values.otp,
    })
      .then(
        (resp: {
          error: { data?: { message?: string }; status: number };
          data?: { token?: string };
        }) => {
          if (resp?.error) {
            setFieldError("otp", resp.error.data.message); // show response error message from server
            setSubmitting(false);
          } else {
            if (resp?.data?.token) {
              const decodedToken: jwtDecodedValue = jwtDecode(
                resp?.data?.token
              );
              const roles =
                decodedToken[
                  "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
                ];
              const name =
                decodedToken[
                  "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"
                ];
              const userId: string = decodedToken?.Id;

              const payload = {
                userName: roles === "SeniorWatch" ? "" : name ?? t("אנונימי"),
                token: resp.data?.token,
                roles: Array.isArray(roles) ? roles : [roles],
                userId,
              };

              dispatch(setCredentials({ ...payload }));
              const initialRole = getInitialRole(
                Array.isArray(roles) ? roles[0] : roles
              );
              initialRole && setselectedRole(initialRole);
              initialRole.value !== UserRole.SENIOR_WATCH
                ? navigate("/reports/inprogress")
                : navigate("/reports/followup");
            }
            setSubmitting(false);
          }
        }
      )
      .catch((err: { data?: { message?: string }; status: number }) => {
        console.error(err);
        if (err.status === 400) {
          // if error status is 400, then the server error message will be displayed
          setFieldError("otp", err.data.message);
          setSubmitting(false);
        } else {
          setFieldError("otp", t("אימות קוד לא תקין"));
          setSubmitting(false);
        }
      });
  };

  return (
    <ThemeProvider dir={i18n.dir()} theme={theme}>
      <div className="d-flex justify-content-center align-items-center container rtl">
        <div className="card py-5 px-3">
          <h1 className="m-0 text-right">{t("otpVerifyTitle")}</h1>
          <p className="text-center">{t("otpEnterCodePrompt")}</p>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleVerifyOtp}
          >
            {({
              isSubmitting,
              handleSubmit,
              handleChange,
              values,
              touched,
              isValid,
              errors,
            }) => {
              return (
                <FormControl
                  className="mt-5"
                  noValidate
                  onSubmit={handleSubmit}
                >
                  <Row className="mb-3">
                    <InputGroup hasValidation>
                      <FormControl.Group
                        as={Col}
                        md="10"
                        controlId="validationOtp"
                      >
                        <FormControl.Label className="text-right">
                          {t("otpCodeLabel")}
                        </FormControl.Label>

                        <FormControl.Control
                          isInvalid={!!errors?.otp}
                          type="text"
                          aria-describedby="otpHelpBlock"
                          disabled={isSubmitting}
                          placeholder="123456"
                          size="lg"
                          name="otp"
                          value={values.otp}
                          onChange={handleChange}
                          isValid={touched.otp && !errors.otp}
                        />
                        {errors?.otp && (
                          <BootstrapForm.Control.Feedback type="invalid">
                            {errors.otp}
                          </BootstrapForm.Control.Feedback>
                        )}
                      </FormControl.Group>
                    </InputGroup>
                  </Row>

                  <Button
                    variant="primary"
                    type="submit"
                    disabled={isSubmitting || !isValid || disabled}
                  >
                    {isSubmitting ? t("otpVerifying") : t("otpVerifyButton")}
                  </Button>
                  <div className="text-center">
                    <ResendLink />
                  </div>
                  <div className="text-center">
                    <Timer
                      setDisabled={setDisabled}
                      timerEnd={timerEnd}
                      setView={setView}
                    />
                  </div>
                </FormControl>
              );
            }}
          </Formik>
        </div>
      </div>
    </ThemeProvider>
  );
};

export default OtpConfirmation;
